import React, { useEffect, useRef, useState } from 'react';
import { Mic, Globe, Users, Calendar } from 'lucide-react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';

const Conferences = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const currentRef = sectionRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  const conferences = [
    {
      type: 'paper',
      title: 'Structural Unity and Narrative Coherence in Surah Hud (Q 11)',
      event: 'IQSA Annual Meeting',
      location: 'Los Angeles, CA, USA',
      date: 'Nov 2025'
    },
    {
      type: 'keynote',
      title: 'IQSA Annual Meeting',
      event: 'International Quranic Studies Association',
      location: 'Agha Khan Centre, London, UK',
      date: 'Jul 2024'
    },
    {
      type: 'workshop',
      title: 'Using Literature in the Language Classroom',
      event: 'Teacher Training Workshops',
      location: 'Usak University, Turkiye',
      date: 'Nov 2019'
    },
    {
      type: 'plenary',
      title: 'From Cramming to Creating: Ideational Frameworks in Teaching and Learning L2 Writing',
      event: '34th SPELT International Conference',
      location: 'Abbottabad, Pakistan',
      date: 'Nov 2018'
    },
    {
      type: 'paper',
      title: 'Interpreting the Quran in the Contemporary Muslim World: A Feminist Discourse Analysis',
      event: '4th International Conference of the Linguistic Association of Pakistan',
      location: 'Rawalpindi, Pakistan',
      date: 'Oct 2018'
    },
    {
      type: 'paper',
      title: 'The Ideological Representation of Self and Other in Post 9/11 Discourse in British Newspapers',
      event: '9th International Corpus Linguistics Conference',
      location: 'University of Birmingham, UK',
      date: 'Jul 2017'
    },
    {
      type: 'paper',
      title: 'Analyzing Supreme Court of Pakistan Judgements with Halliday Textual Metafunction',
      event: 'Faces of English 2: Teaching and Researching Academic English',
      location: 'Hong Kong University, Hong Kong',
      date: 'Jun 2017'
    },
    {
      type: 'panel',
      title: 'Relevance of Shakespeare in Contemporary World',
      event: 'Panel Discussion',
      location: 'IIUI, Islamabad, Pakistan',
      date: 'Dec 2016'
    },
    {
      type: 'paper',
      title: 'Seeds of Conflict: How have Biblical Realism and Quranic Idealism Shaped World Views?',
      event: 'The New Global City Conference',
      location: 'University of North Carolina, USA',
      date: 'Mar 2016'
    },
    {
      type: 'paper',
      title: 'Breaking the Myths of Resistance: Teaching English in Madrassa',
      event: 'Religious Education in Pakistan Conference',
      location: 'IIUI, Islamabad, Pakistan',
      date: 'Mar 2014'
    },
    {
      type: 'paper',
      title: 'Stylistic Variations and Ideologies of English Translations of the Quran',
      event: 'International Conference on Problems of Quran Translation',
      location: 'IIUI, Islamabad, Pakistan',
      date: 'Apr 2013'
    },
    {
      type: 'paper',
      title: 'From the Word of God to the Words of Men: The Quran and the Poetics of Translation',
      event: 'From Reflection to Refraction Conference',
      location: 'University of Manchester, UK',
      date: 'Oct 2010'
    },
    {
      type: 'paper',
      title: 'A Discourse Approach to Spoken and Written English',
      event: '23rd SPELT International Conference',
      location: 'Rawalpindi, Pakistan',
      date: 'Nov 2007'
    },
    {
      type: 'keynote',
      title: 'The Fourth Biennial Conference: The Quran - Text, Interpretation and Translation',
      event: 'SOAS Conference',
      location: 'University of London, UK',
      date: 'Nov 2005'
    },
    {
      type: 'paper',
      title: 'Dream Motifs in Quranic and Biblical Narrative',
      event: 'Conference on Language and Myth',
      location: 'University of Manchester, UK',
      date: 'Sep 2001'
    }
  ];

  const getTypeIcon = (type) => {
    switch(type) {
      case 'keynote': return Mic;
      case 'plenary': return Users;
      case 'workshop': return Globe;
      default: return Calendar;
    }
  };

  const getTypeBadge = (type) => {
    const badges = {
      keynote: { label: 'Keynote', color: 'bg-blue-600 text-white' },
      plenary: { label: 'Plenary', color: 'bg-cyan-600 text-white' },
      paper: { label: 'Paper', color: 'bg-blue-500 text-white' },
      workshop: { label: 'Workshop', color: 'bg-cyan-500 text-white' },
      panel: { label: 'Panel', color: 'bg-blue-400 text-white' }
    };
    return badges[type] || { label: 'Presentation', color: 'bg-gray-500 text-white' };
  };

  return (
    <section id="conferences" ref={sectionRef} className="py-16 bg-gradient-to-b from-blue-50 to-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-3">
            Conference Presentations
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-cyan-500 mx-auto mb-4"></div>
          <p className="text-gray-600 max-w-2xl mx-auto text-sm">
            International conferences, keynotes, and academic presentations
          </p>
        </div>

        <div className="max-w-5xl mx-auto">
          <div className="space-y-4">
            {conferences.map((conf) => {
              const Icon = getTypeIcon(conf.type);
              const typeBadge = getTypeBadge(conf.type);
              
              return (
                <Card
                  key={`${conf.title}-${conf.date}`}
                  className={`p-5 border-l-4 border-blue-600 hover:shadow-xl transition-all duration-500 bg-white ${
                    isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <Icon className="text-white" size={18} />
                    </div>

                    <div className="flex-grow">
                      <Badge className={`${typeBadge.color} px-2.5 py-0.5 text-xs mb-2`}>
                        {typeBadge.label}
                      </Badge>

                      <h3 className="text-base font-bold text-gray-900 mb-1.5 leading-snug">
                        {conf.title}
                      </h3>

                      <p className="text-sm text-blue-700 font-semibold mb-1.5">
                        {conf.event}
                      </p>

                      <div className="flex flex-wrap gap-3 text-xs text-gray-600">
                        <span className="flex items-center gap-1">
                          <Globe size={14} className="text-cyan-500" />
                          {conf.location}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar size={14} className="text-cyan-500" />
                          {conf.date}
                        </span>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12 max-w-4xl mx-auto">
          <div className="text-center p-4 bg-white rounded-xl shadow-md border-t-4 border-blue-600">
            <div className="text-2xl font-bold text-blue-600 mb-1">30+</div>
            <div className="text-xs text-gray-700">International Conferences</div>
          </div>
          <div className="text-center p-4 bg-white rounded-xl shadow-md border-t-4 border-cyan-500">
            <div className="text-2xl font-bold text-cyan-600 mb-1">10+</div>
            <div className="text-xs text-gray-700">Keynote Addresses</div>
          </div>
          <div className="text-center p-4 bg-white rounded-xl shadow-md border-t-4 border-blue-600">
            <div className="text-2xl font-bold text-blue-600 mb-1">5+</div>
            <div className="text-xs text-gray-700">Workshops Led</div>
          </div>
          <div className="text-center p-4 bg-white rounded-xl shadow-md border-t-4 border-cyan-500">
            <div className="text-2xl font-bold text-cyan-600 mb-1">8+</div>
            <div className="text-xs text-gray-700">Countries</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Conferences;
